import React from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { StatusDot, TableEmptyState } from '../../common/pageKit';
import { formatINR } from '../../../dashboard/primitives';
import { AtRiskDeals } from '../../../types/salesAnalytics';
import { daysUntil, formatDateIN } from '../../../pages/sales/salesReportsHelpers';

interface AtRiskSectionProps {
  atRisk: AtRiskDeals | null;
  loading: boolean;
}

const Row: React.FC<{ title: string; subtitle: string; action: string; tone: 'error' | 'warning'; value?: string }> = ({
  title,
  subtitle,
  action,
  tone,
  value,
}) => (
  <Box sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {value && (
        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {value}
        </Typography>
      )}
    </Box>
    <Box sx={{ mt: 0.5 }}>
      <StatusDot label={action} tone={tone} />
    </Box>
  </Box>
);

/**
 * Leads with what to do about each deal rather than a bare list — an
 * "at-risk" report nobody acts on is just a longer report.
 */
const AtRiskSection: React.FC<AtRiskSectionProps> = ({ atRisk, loading }) => {
  if (loading || !atRisk) {
    return <Box sx={{ minHeight: 200, mb: 3 }} />;
  }

  const { overdueOpportunities, staleHighValue, expiringQuotations } = atRisk;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 1.5 }}>At-Risk Deals</Typography>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Overdue Opportunities
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Past their expected closure date and still open.
            </Typography>
            {overdueOpportunities.length === 0 ? (
              <TableEmptyState title="Nothing overdue" />
            ) : (
              overdueOpportunities.map((o) => (
                <Row
                  key={o.id}
                  title={o.opportunityName}
                  subtitle={`${o.customerName ?? 'Unknown customer'} · ${o.assignedToName ?? 'Unassigned'} · due ${formatDateIN(o.expectedClosureDate)}`}
                  value={formatINR(o.estimatedValue, true)}
                  action={`Follow up — ${o.daysOverdue}d overdue`}
                  tone="error"
                />
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Stale High-Value Deals
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Above the average open deal size with no activity in 14+ days.
            </Typography>
            {staleHighValue.length === 0 ? (
              <TableEmptyState title="Nothing stale" />
            ) : (
              staleHighValue.map((o) => (
                <Row
                  key={o.id}
                  title={o.opportunityName}
                  subtitle={`${o.customerName ?? 'Unknown customer'} · ${o.assignedToName ?? 'Unassigned'} · last activity ${
                    o.lastActivityAt ? formatDateIN(o.lastActivityAt) : 'never'
                  }`}
                  value={formatINR(o.estimatedValue, true)}
                  action="Re-engage — no recent activity"
                  tone="warning"
                />
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2, height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Expiring Quotations
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Sent quotations valid for 7 more days or fewer.
            </Typography>
            {expiringQuotations.length === 0 ? (
              <TableEmptyState title="Nothing expiring soon" />
            ) : (
              expiringQuotations.map((q) => {
                const until = daysUntil(q.validUntil);
                return (
                  <Row
                    key={q.id}
                    title={q.quotationNumber}
                    subtitle={`${q.customerName ?? 'Unknown customer'} · valid until ${formatDateIN(q.validUntil)}`}
                    value={formatINR(q.totalAmount, true)}
                    action={until !== null ? `Renew or close — ${Math.max(until, 0)}d left` : 'Renew or close'}
                    tone="warning"
                  />
                );
              })
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AtRiskSection;
